import { config } from '@euler/config';
import { Base64 } from 'js-base64';
import { StorageService } from './storage.service';

/**
 * @class Auth token related service class.
 */
export class TokenService {
  private _token: string | null;

  constructor(private readonly storage: StorageService) {
    this.storage = storage;
    this._token = null;
  }

  getTokenStorageKey() {
    let env: string = config.environment;
    if (/prod/i.test(env)) env = '';
    return [env, '__authtoken__'].filter(x => x).join('.');
  }

  /**
   * Get the current user's token.
   * @returns {String | null} token
   */
  async getToken(): Promise<string | null> {
    if (this._token) return this._token;
    return await this.storage.get<string>(this.getTokenStorageKey());
  }

  /**
   * Set the new token for the given user.
   * @param {String} token the token to set for the user.
   */
  async setToken(token: string): Promise<void> {
    await this.storage.set(this.getTokenStorageKey(), token);
    this._token = token;
  }

  /**
   * Set token by key.
   * @param key token key
   * @param token token value
   */
  async setTokenByKey(key: string, token: string): Promise<void> {
    await this.storage.set(key, token);
  }

  /**
   * Get token value by key.
   * @param key token key
   * @returns token value if exists otherwise null.
   */
  async getTokenByKey(key: string): Promise<string | null> {
    return await this.storage.get<string>(key);
  }

  /**
   * Remove token by key.
   * @param key token key
   */
  async removeTokenByKey(key: string): Promise<void> {
    await this.storage.remove(key);
  }

  /**
   * Remove token.
   */
  async removeToken(): Promise<void> {
    await this.storage.remove(this.getTokenStorageKey());
    this._token = null;
  }

  getRefreshTokenStorageKey(): string {
    let env: string = config.environment;
    if (/prod/i.test(env)) env = '';
    return [env, '__refreshtoken__'].filter(x => x).join('.');
  }

  async getRefreshToken(): Promise<string | null> {
    return await this.storage.get<string>(this.getRefreshTokenStorageKey());
  }

  async setRefreshToken(refreshToken: string): Promise<void> {
    await this.storage.set(this.getRefreshTokenStorageKey(), refreshToken);
  }

  async removeRefreshToken(): Promise<void> {
    await this.storage.remove(this.getRefreshTokenStorageKey());
    await this.removeRefreshTokenExpiry();
  }

  getRefreshTokenExpiryKey(): string {
    let env: string = config.environment;
    if (/prod/i.test(env)) env = '';
    return [env, '__refreshtoken_expiry__'].filter(x => x).join('.');
  }

  async setRefreshTokenExpiry(expiresAt: number): Promise<void> {
    await this.storage.set(this.getRefreshTokenExpiryKey(), expiresAt);
  }

  async getRefreshTokenExpiry(): Promise<number | null> {
    return await this.storage.get<number>(this.getRefreshTokenExpiryKey());
  }

  async removeRefreshTokenExpiry(): Promise<void> {
    await this.storage.remove(this.getRefreshTokenExpiryKey());
  }

  /**
   * Decode the token payload.
   * @param token {string} decode the base64 encoded jwt token string.
   * @returns {any} token data.
   */
  decodeTokenPayload(token: string): any {
    const encodedData = token.replace('Bearer ', '').split('.')[1];
    const data = Base64.decode(encodedData);
    return JSON.parse(data);
  }

  /**
   * Verify if the given token is valid.
   * @param token {string} the token to verify
   * @returns {boolean} true if valid otherwise false.
   */
  isValidToken(token: string): boolean {
    try {
      const payload = this.decodeTokenPayload(token);
      const expireTime = payload.exp * 1000;
      return Date.now() < expireTime;
    } catch (e) {
      console.error(`error decode token "${token}": `, e);
      /* noop */
    }
    return false;
  }

  /**
   * Get token expire time.
   * @returns {Date|Number} expire time.
   */
  async getTokenExpiry(): Promise<number | undefined> {
    const token = await this.getToken();
    if (!token) return;
    const payload = this.decodeTokenPayload(token);
    return payload.exp;
  }
}
